import React, { useEffect, useState } from "react";
import { Logo } from "./Logo";
import { NAV_LINKS, SCHOOL } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { Phone, MessageCircle, Menu } from "lucide-react";

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="top-nav"
      className={`sticky top-0 z-50 border-b transition-colors ${
        scrolled
          ? "bg-white/70 backdrop-blur-xl backdrop-saturate-150 border-maple-border/60"
          : "bg-white/40 backdrop-blur-md border-transparent"
      }`}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-28 sm:h-32 flex items-center justify-between gap-4">
        <Logo />

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-6" aria-label="Primary">
          {NAV_LINKS.map((link) => (
            <a
              key={link.id}
              href={`#${link.id}`}
              data-testid={`nav-link-${link.id}`}
              className="text-sm font-medium text-maple-navy/80 hover:text-maple-navy transition-colors"
            >
              {link.label}
            </a>
          ))}
        </nav>

        {/* Mobile menu (hamburger only — Call/WhatsApp moved to mobile sheet & floating button) */}
        <div className="lg:hidden flex items-center gap-2">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="border-maple-border text-maple-navy"
                data-testid="mobile-menu-open-button"
                aria-label="Open menu"
              >
                <Menu className="h-5 w-5" aria-hidden="true" />
              </Button>
            </SheetTrigger>
            <SheetContent
              side="right"
              className="bg-maple-cream border-maple-border w-[85vw] sm:w-[380px]"
              data-testid="mobile-menu-sheet"
            >
              <SheetTitle className="sr-only">Site navigation</SheetTitle>
              <div className="flex flex-col h-full">
                <div className="mt-2 mb-6">
                  <Logo />
                </div>
                <nav className="flex flex-col gap-1" aria-label="Mobile primary">
                  {NAV_LINKS.map((link) => (
                    <a
                      key={link.id}
                      href={`#${link.id}`}
                      onClick={() => setOpen(false)}
                      data-testid={`mobile-nav-link-${link.id}`}
                      className="py-3 px-3 rounded-lg text-maple-navy hover:bg-maple-cream2 text-base font-medium"
                    >
                      {link.label}
                    </a>
                  ))}
                </nav>
                <div className="mt-auto pt-6 flex flex-col gap-2">
                  <Button
                    asChild
                    className="lift-on-hover w-full bg-maple-navy text-maple-cream hover:bg-maple-navy2"
                    data-testid="mobile-call-button"
                  >
                    <a href={SCHOOL.phoneLink}>
                      <Phone className="mr-2 h-4 w-4" aria-hidden="true" />
                      Call {SCHOOL.phoneDisplay}
                    </a>
                  </Button>
                  <Button
                    asChild
                    variant="outline"
                    className="lift-on-hover w-full border-maple-navy/30 text-maple-navy hover:bg-maple-cream2"
                    data-testid="mobile-whatsapp-button"
                  >
                    <a href={SCHOOL.whatsappLink} target="_blank" rel="noopener noreferrer">
                      <MessageCircle className="mr-2 h-4 w-4" aria-hidden="true" />
                      WhatsApp Us
                    </a>
                  </Button>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
